import { useState, useEffect, useRef } from 'react';
import type { ProjectSnapshot } from '../model/types';
import { getProjectLibrary, saveProjectLibrary } from './project';

/**
 * Legacy localStorage key. Historically THIS hook wrote the project library to
 * localStorage while the Zustand store wrote version snapshots / autosave to
 * IndexedDB (idb-keyval) under the same `bg2_library` key. The two backends never
 * met, so `commitVersion` silently no-op'd and "Точек сохранения нет" stuck forever
 * (data-loss bug). We now delegate the whole hook to the IndexedDB source of truth
 * (`getProjectLibrary`/`saveProjectLibrary` in project.ts) and absorb any legacy
 * localStorage entries into IDB once, so nothing the user already saved is lost.
 */
const LEGACY_LS_KEY = 'bg2_library';

/** Safe epoch ms from a snapshot's lastModified (0 if missing/invalid) — for merge conflict resolution. */
function modifiedTime(p: ProjectSnapshot): number {
  const ts = p.lastModified ? new Date(p.lastModified).getTime() : 0;
  return Number.isFinite(ts) ? ts : 0;
}

/**
 * Data-safe, one-time unification: merge any legacy `localStorage['bg2_library']`
 * entries into the IndexedDB-backed library (newest `lastModified` wins on id
 * collision), persist the merged result to IDB, then drop the legacy localStorage
 * copy so it can never shadow the IDB source again. Returns the effective library.
 * Exported for unit testing of the merge logic.
 */
export async function loadUnifiedLibrary(): Promise<ProjectSnapshot[]> {
  const idbLib = await getProjectLibrary();

  let legacy: ProjectSnapshot[] = [];
  try {
    const raw = localStorage.getItem(LEGACY_LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) legacy = parsed;
    }
  } catch {
    legacy = []; // corrupt legacy blob — ignore, never throw
  }

  if (legacy.length === 0) return idbLib;

  // Merge by id; on collision keep the entry with the newer lastModified.
  const byId = new Map<string, ProjectSnapshot>();
  for (const p of [...idbLib, ...legacy]) {
    const key = String(p.id);
    const existing = byId.get(key);
    if (!existing || modifiedTime(p) > modifiedTime(existing)) {
      byId.set(key, p);
    }
  }
  const merged = Array.from(byId.values());

  await saveProjectLibrary(merged);
  try {
    localStorage.removeItem(LEGACY_LS_KEY);
  } catch {
    // best-effort — removal failure is non-fatal (IDB is already the source of truth)
  }
  return merged;
}

/**
 * Id-keyed merge of an incoming snapshot into the current library: the incoming
 * snapshot goes to the front, followed by every existing entry whose id differs.
 * Exported for unit testing. Keys on id (not name) to match the three sibling
 * operations below (deleteProject/renameProject/getProjectById) and
 * loadUnifiedLibrary's own collision map above. Ids are compared as strings
 * because createProjectSnapshot generates a numeric id, while a snapshot that
 * has round-tripped through JSON (e.g. from a backup restore) can carry the
 * same id as a string — a loose comparison would otherwise duplicate the
 * project (SAFE-01).
 */
export function mergeProjectIntoLibrary(
  prev: ProjectSnapshot[],
  incoming: ProjectSnapshot
): ProjectSnapshot[] {
  return [incoming, ...prev.filter((p) => String(p.id) !== String(incoming.id))];
}

export function useProjectLibrary() {
  const [library, setLibrary] = useState<ProjectSnapshot[]>([]);
  const hydratedRef = useRef(false);

  // Hydrate once from IndexedDB, absorbing any legacy localStorage library on first run.
  useEffect(() => {
    let cancelled = false;
    loadUnifiedLibrary().then((lib) => {
      if (!cancelled) {
        hydratedRef.current = true;
        setLibrary(lib);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-sync when another context mutates the library: commitVersion / autosave /
  // other tabs dispatch a 'storage' event after writing IDB (see project.ts).
  useEffect(() => {
    const handleStorageChange = () => {
      getProjectLibrary().then((lib) => setLibrary(lib));
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const addProject = (projectData: ProjectSnapshot) => {
    setLibrary((prev) => {
      const next = mergeProjectIntoLibrary(prev, projectData);
      void saveProjectLibrary(next); // idempotent write to IDB; dispatches 'storage' for other instances
      return next;
    });
  };

  const deleteProject = (id: string | number) => {
    setLibrary((prev) => {
      const next = prev.filter((p) => String(p.id) !== String(id));
      void saveProjectLibrary(next);
      return next;
    });
  };

  const renameProject = (id: string | number, newName: string) => {
    setLibrary((prev) => {
      const next = prev.map((p) => (String(p.id) === String(id) ? { ...p, name: newName } : p));
      void saveProjectLibrary(next);
      return next;
    });
  };

  const getProjectById = (id: string | number) => {
    return library.find((p) => String(p.id) === String(id));
  };

  return {
    library,
    addProject,
    deleteProject,
    renameProject,
    getProjectById
  };
}
